'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  faAngleDown,
  faGavel,
  faSearch,
  faSliders,
} from '@fortawesome/pro-solid-svg-icons'
import type { GridApi } from 'ag-grid-community'
import dynamic from 'next/dynamic'
import { styled } from 'styled-components'
import { useBoolean } from 'usehooks-ts'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  EmptyState,
  FormattedMessage,
  Pagination,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints } from '@b-stock/fe-core/design-system'
import type {
  StoreFrontDto,
  StoreFrontOptionsColumnLayoutDto,
} from '@b-stock/listing-api-client'

import ProductStructuredData from '@/components/tracking/ProductStructuredData'
import { FeatureFlags } from '@/helpers/featureFlags'
import { ItemListProvider } from '@/helpers/telemetry/itemListContext'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useViewItemList } from '@/helpers/telemetry/useViewItemList'
import { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'
import newSellerStorefrontsQuery from '@/queries/newSellerStorefrontsQuery'

import type { ListingGridEmptyState } from '../ListingGrid/ListingGrid'
import { MyBidsFilterToggle } from '../ListingSearchView/MyBidsFilterToggle'
import { ViewToggle } from '../ListingSearchView/ViewToggle'
import { AppliedFilters } from '../search/AppliedFilters'
import { useBulkSelection } from '../search/BulkSelectionProvider'
import {
  useListingSearch,
  useListingSearchPusherUpdates,
} from '../search/ListingSearchProvider'
import PerPageDropdown from '../search/perPageDropdown/PerPageDropdown'
import { useAllListingsFilters } from '../search/SearchFilters'
import FiltersModal from '../search/SearchFilters/FiltersModal'
import SortByDropdown from '../search/sortByDropdown/SortByDropdown'
import StorefrontListingSearch from '../search/StorefrontListingSearch'
import {
  BulkActionsDropdown,
  BulkSelectionBanner,
  useClearBulkSelectionOnFilterChange,
} from '../shared/BulkActionsBar'
import { BulkBidModal } from '../shared/BulkBidModal'
import FiltersTopBar from '../shared/FiltersTopBar'
import {
  FiltersButton,
  FiltersButtonIcon,
  ListingsInnerContainer,
  ListingsOuterContainer,
  PaginationWrapper,
  ResultsRow,
  SearchSortControls,
  TotalListings,
} from '../shared/listings-ui'
import { useIsMobileTableLayout } from '../shared/useIsMobileTableLayout'
import { useThresholdEmptyState } from '../shared/useThresholdEmptyState'
import SimilarInventory from '../SimilarInventory'
import { DealGraphButton, DealGraphModal } from './DealGraph'
import { ExportListViewButton } from './ExportListViewButton'
import { FreezeSortBanner } from './FreezeSortBanner'
import { FreezeSortToggle } from './FreezeSortToggle'
import { getListViewSurface } from './helpers/listViewSurface'
import { ActionPanelProvider } from './ListViewActionPanel'
import { ListViewNewSellerProvider } from './ListViewNewSellerContext'
import { ListViewTableSkeleton } from './ListViewTableSkeleton'
import { ListViewTooltip } from './ListViewTooltip'
import PaymentEntitiesPrefetcher from './PaymentEntitiesPrefetcher'
import type { ColumnLayoutIdEnum } from './types'
import { UploadBidSheetButton } from './UploadBidSheet'

const baseUrl =
  typeof window !== 'undefined' ? window.location.origin : 'https://bstock.com'

// Hide on desktop to prevent gap at left
const FiltersBarContainerEquivalentMobileGapMaker = styled.div`
  display: block;

  @media ${Breakpoints.min.large} {
    display: none;
  }
`

/*
 * Lazy: `ListViewTable` is the only runtime importer of AG Grid on this page,
 * and Grid View — the default — never renders it. Deferring keeps the grid out
 * of the All Listings page's initial JavaScript for every buyer who does not
 * switch to List View.
 */
const ListViewTable = dynamic(() => import('./ListViewTable'), {
  loading: () => <ListViewTableSkeleton />,
  ssr: false,
})

export function ListView({
  columnLayoutId,
  customColumnLayout,
  emptyState,
  enableManifestExport,
  excludeSavedSearch,
  resultsTitle,
  storefront,
}: {
  /** Replaces the "N Results" heading above the table. */
  resultsTitle?: React.ReactNode
  columnLayoutId?: ColumnLayoutIdEnum
  /** Replaces the generic "no results" state; My Bids and threshold still win. */
  emptyState?: ListingGridEmptyState
  customColumnLayout?: StoreFrontOptionsColumnLayoutDto
  enableManifestExport?: boolean
  excludeSavedSearch?: boolean
  storefront?: StoreFrontDto
}) {
  const { trackButtonClicked } = useAnalytics()
  const isMobileFiltersShown = useIsMobileTableLayout()

  const searchCtx = useListingSearch()
  const {
    data,
    error,
    isLoading,
    myBidsFilter,
    setPage,
    state,
    updateFilters,
  } = searchCtx
  const isMyBidsLoading = !!state.filters.myBids && myBidsFilter.isLoading

  const listings = useMemo(() => data?.listings ?? [], [data?.listings])

  const facadeListings = useMemo(
    () => listings.map((raw) => new CombinedListingSearchResult(raw)),
    [listings]
  )

  // While the myBids id fetch is in flight, don't render the stale
  // (unfiltered) rows underneath the loading state — clear them so the
  // grid's loading overlay/skeleton shows immediately instead of the old
  // list lingering until the new results arrive.
  const visibleFacadeListings = isMyBidsLoading ? [] : facadeListings

  const { changedListingIds, clearChangedListingIds } =
    useListingSearchPusherUpdates(searchCtx)

  const { filters: allAppliedFilters } = useAllListingsFilters(storefront)
  const showRelevance = !!state.filters.q

  useClearBulkSelectionOnFilterChange(state.filters)

  const {
    value: showMobileFilterModal,
    setTrue: openMobileFilterModal,
    setFalse: closeMobileFilterModal,
  } = useBoolean(false)

  const itemListId = storefront
    ? `seller-storefront-listview-${storefront._id}`
    : 'all-auctions-list'

  const itemListName = storefront
    ? `Seller Storefront List: ${storefront.name}`
    : 'All Auctions - List View'

  const freezeSortPageType = storefront ? 'storefront' : 'alp'
  const enableFreezeSort = useFlag(FeatureFlags.ENABLE_FREEZE_SORT)
  const isNewSellerTreatment =
    useFlag(FeatureFlags.NEW_SELLER_ALP_LABEL) === 'treatment'
  const { data: newSellerIds, isError: isNewSellerIdsError } =
    newSellerStorefrontsQuery.useWithOpts([], {
      enabled: isNewSellerTreatment,
    })

  const isNewSellerFn = useCallback(
    (storefrontId: string) =>
      isNewSellerTreatment && (newSellerIds?.has(storefrontId) ?? false),
    [isNewSellerTreatment, newSellerIds]
  )

  const enableDealGraph = useFlag(FeatureFlags.ENABLE_ALP_DEAL_GRAPH)
  const [isDealGraphActive, setIsDealGraphActive] = useState(false)

  const thresholdEmptyState = useThresholdEmptyState()
  const showDealGraph = enableDealGraph && isDealGraphActive

  useViewItemList({
    listings,
    itemListId,
    itemListName,
    enabled:
      !isLoading &&
      !error &&
      listings.length > 0 &&
      (!isNewSellerTreatment ||
        newSellerIds !== undefined ||
        isNewSellerIdsError),
    maxItems: 100, // List view by default shows more at one time. So higher max value.
    isNewSeller: (listing) => isNewSellerFn(listing.storefrontId),
  })

  const [gridApi, setGridApi] =
    useState<GridApi<CombinedListingSearchResult> | null>(null)

  const { selectedIds, clear, toggle } = useBulkSelection()
  const [isBulkBidModalOpen, setIsBulkBidModalOpen] = useState(false)

  // Accumulate selected listings across pages: add from the current page,
  // remove anything that has been deselected.
  const listingsRef = useRef<HTMLDivElement>(null)
  const selectedListingsCacheRef = useRef<
    Map<string, CombinedListingSearchResult>
  >(new Map())
  const [selectedListings, setSelectedListings] = useState<
    CombinedListingSearchResult[]
  >([])
  useEffect(() => {
    const cache = selectedListingsCacheRef.current
    for (const listing of facadeListings) {
      if (selectedIds.has(listing.listingId)) {
        cache.set(listing.listingId, listing)
      }
    }
    for (const id of [...cache.keys()]) {
      if (!selectedIds.has(id)) {
        cache.delete(id)
      }
    }
    setSelectedListings(Array.from(cache.values()))
  }, [facadeListings, selectedIds])

  const handlePageChange = (page: number) => {
    trackButtonClicked({ buttonName: 'pagination', page })
    setPage(page)
  }

  const surface = useMemo(
    () => getListViewSurface({ columnLayoutId, customColumnLayout }),
    [columnLayoutId, customColumnLayout]
  )

  const listViewTable = (
    <ListViewNewSellerProvider value={isNewSellerFn}>
      <ListViewTable
        changedListingIds={changedListingIds}
        clearChangedListingIds={clearChangedListingIds}
        columnLayoutId={columnLayoutId}
        customColumnLayout={customColumnLayout}
        error={error}
        isLoading={isLoading || isMyBidsLoading}
        listings={visibleFacadeListings}
        setGridApi={setGridApi}
      />
    </ListViewNewSellerProvider>
  )

  return (
    <>
      <PaymentEntitiesPrefetcher />
      <ProductStructuredData baseUrl={baseUrl} listings={listings} />
      <FiltersModal
        closeFilterModal={closeMobileFilterModal}
        filters={allAppliedFilters}
        searchCtx={searchCtx}
        showFilterModal={showMobileFilterModal}
        showFreezeSort={isMobileFiltersShown && enableFreezeSort}
        showRelevance={showRelevance}
        showSortBy={isMobileFiltersShown}
        showStorefrontSearch={isMobileFiltersShown && !!storefront}
        slideInPanel={!isMobileFiltersShown}
        storefront={storefront}
      />
      <ListingsOuterContainer ref={listingsRef}>
        <FiltersBarContainerEquivalentMobileGapMaker />
        <ListingsInnerContainer>
          <FiltersTopBar>
            <AppliedFilters
              excludeSavedSearch={excludeSavedSearch}
              filters={allAppliedFilters}
              searchCtx={searchCtx}
            />

            <ResultsRow>
              <BulkActionsDropdown
                onBulkBid={() => setIsBulkBidModalOpen(true)}
                selectedListings={selectedListings}
              />

              <TotalListings data-testid="ResultsCount">
                {resultsTitle ?? (
                  <FormattedMessage
                    id="Listings.results"
                    values={{ count: isLoading ? '' : data?.total || 0 }}
                  />
                )}
                <ListViewTooltip />
              </TotalListings>

              <SearchSortControls>
                <MyBidsFilterToggle />
                {!isMobileFiltersShown && (
                  <>
                    {storefront && (
                      <StorefrontListingSearch storefront={storefront} />
                    )}
                    <PerPageDropdown />
                    <SortByDropdown showRelevance={showRelevance} />
                    <FreezeSortToggle pageType={freezeSortPageType} />
                  </>
                )}
                <FiltersButton
                  appearance="secondary"
                  onClick={openMobileFilterModal}
                >
                  <FiltersButtonIcon icon={faSliders} />
                  <span>
                    <FormattedMessage id="Filters.Mobile.FiltersButton.title" />
                  </span>
                  <FiltersButtonIcon icon={faAngleDown} />
                </FiltersButton>
                <ExportListViewButton
                  gridApi={gridApi}
                  includeManifests={enableManifestExport}
                  resultCount={data?.total}
                  state={state}
                  storefront={storefront}
                  surface={surface}
                />
                {enableDealGraph && (
                  <DealGraphButton
                    /*
                     * Nothing to plot means the EmptyState below wins over the
                     * table, so opening the graph would show an empty plot of
                     * nothing.
                     */
                    disabled={!data?.total}
                    onOpen={() => setIsDealGraphActive(true)}
                  />
                )}
                <UploadBidSheetButton
                  selectedListings={selectedListings}
                  state={state}
                  storefront={storefront}
                  surface={surface}
                />
                <ViewToggle />
              </SearchSortControls>
            </ResultsRow>
            <BulkSelectionBanner />
            <FreezeSortBanner pageType={freezeSortPageType} />
            {isBulkBidModalOpen && (
              <BulkBidModal
                listings={selectedListings}
                storefrontSlug={storefront?.slug}
                onClose={() => setIsBulkBidModalOpen(false)}
                onSuccess={(failedListingIds) => {
                  clear()
                  failedListingIds.forEach((id) => toggle(id))
                }}
              />
            )}
          </FiltersTopBar>

          {data?.total === 0 && !isMyBidsLoading ? (
            state.filters.myBids ? (
              <EmptyState
                icon={faGavel}
                title={
                  <FormattedMessage id="Listings.myBidsFilter.empty.title" />
                }
                description={
                  <FormattedMessage id="Listings.myBidsFilter.empty.description" />
                }
                primaryButton={{
                  label: (
                    <FormattedMessage id="Listings.myBidsFilter.empty.cta" />
                  ),
                  handleClick: () => updateFilters({ myBids: false }),
                }}
              />
            ) : thresholdEmptyState ? (
              // No results with a threshold above the minimum — offer to lower it
              <EmptyState {...thresholdEmptyState} />
            ) : emptyState ? (
              <EmptyState {...emptyState} />
            ) : (
              <EmptyState
                icon={faSearch}
                title={<FormattedMessage id="Listings.noResults" />}
              />
            )
          ) : (
            <ItemListProvider
              itemListId={itemListId}
              itemListName={itemListName}
            >
              <ActionPanelProvider surface={surface}>
                {listViewTable}
              </ActionPanelProvider>
              {showDealGraph && (
                <DealGraphModal
                  error={error}
                  gridApi={gridApi}
                  isFetching={isLoading || isMyBidsLoading || !gridApi}
                  onClose={() => setIsDealGraphActive(false)}
                />
              )}
            </ItemListProvider>
          )}

          {!!data?.total && (
            <PaginationWrapper>
              <Pagination
                page={state.page}
                perPage={data.limit}
                setPage={handlePageChange}
                total={data.total}
              />
            </PaginationWrapper>
          )}
          <SimilarInventory storefront={storefront} scrollRef={listingsRef} />
        </ListingsInnerContainer>
      </ListingsOuterContainer>
    </>
  )
}
